import { QRCodeSVG } from 'qrcode.react';

import logo from 'src/assets/logo.svg';
import { Logo } from 'src/Components/Logo';
import { SlateRenderer } from 'src/Components/Shared/SlateRenderer';
import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import { AttachmentDto } from 'src/Services/API/generated';

import { Props } from './PrintableInstruction.types';

// All styles are inline — this component is rendered into an isolated iframe

const TEAL = '#114B50';
const FONT = "'Public Sans', Arial, sans-serif";

const PrintAttachments = ({ attachments }: { attachments: AttachmentDto[] }) => {
  const images = attachments.filter(a => a.urls?.previewMediumUrl && !a.video);
  const videos = attachments.filter(a => !!a.video);
  if (images.length === 0 && videos.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
      {images.map(a => (
        <img
          key={a.id}
          src={a.urls.previewMediumUrl}
          alt={a.name}
          style={{ maxHeight: '160px', maxWidth: '200px', objectFit: 'cover', borderRadius: '4px' }}
        />
      ))}
      {videos.map(a => (
        <div
          key={a.id}
          style={{
            height: '80px',
            width: '140px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            color: '#888',
            textAlign: 'center',
            padding: '8px',
          }}
        >
          📹 {a.name}
        </div>
      ))}
    </div>
  );
};

const PrintableInstruction = ({ instruction, assetName, exportUrl }: Props) => {
  return (
    <div
      style={{
        width: '794px',
        minHeight: '1123px',
        fontFamily: FONT,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        backgroundColor: 'white',
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: TEAL,
          padding: '16px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Logo variant="icon" alt="" style={{ height: 36, width: 36 }} />
        <span
          style={{ color: 'white', fontSize: '18px', fontWeight: '600', letterSpacing: '0.5px' }}
        >
          Share Heritage
        </span>
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginLeft: 'auto' }}>
          {assetName}
        </span>
      </div>

      {/* Title */}
      <div
        style={{
          padding: '36px 48px 28px',
          borderBottom: `2px solid ${TEAL}`,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: '28px',
            fontWeight: '700',
            color: TEAL,
            textAlign: 'center',
            lineHeight: '1.3',
          }}
        >
          {instruction.title}
        </h1>
        {instruction.attachment && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
            <PrintAttachments attachments={[instruction.attachment]} />
          </div>
        )}
      </div>

      {/* Sections */}
      <div style={{ flex: 1, padding: '28px 48px' }}>
        {instruction.sections.map((section, index) => (
          <div key={section.id} style={{ marginBottom: '28px' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: TEAL,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: '700',
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: TEAL }}>
                {section.title}
              </h2>
            </div>
            <div
              style={{ paddingLeft: '40px', color: '#333', fontSize: '14px', lineHeight: '1.7' }}
            >
              <SlateRenderer value={section.content as SlateNode[]} />
              {section.attachments?.length > 0 && (
                <PrintAttachments attachments={section.attachments} />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer with QR code */}
      <div
        style={{
          padding: '20px 48px',
          borderTop: '1px solid #ddd',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <QRCodeSVG
          value={exportUrl}
          size={80}
          level="H"
          imageSettings={{ src: logo, height: 16, width: 16, excavate: true }}
          fgColor={TEAL}
        />
        <div>
          <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '600', color: TEAL }}>
            Retrouvez cette consigne en version numérique
          </p>
          <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>{assetName}</p>
        </div>
      </div>
    </div>
  );
};

export default PrintableInstruction;
